// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useOrganizationsStore from '@/app/store/modules/organizations';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsOrganizations from '../../../api/actionsOrganizations';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Switch from '@/common-utils/components/ui/switch';
import { Card } from '@/common-utils/components/ui/card';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Input } from '@/common-utils/components/ui/input';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @returns {JSX} ModalDeleteEmail
 */
function ModalConfigureAlerts({ isOpen, setIsOpen }) {

    // Store
    const { simAlerts, storeRefetchSimAlerts } = useOrganizationsStore();
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    // Toast
    const { toast } = useToast();

    // Constants
    const cardsContent = [
        { title: 'modal.configure.sim_cards.title', key: 'sim-cards', message: 'modal.configure.sim_cards.message' },
        { title: 'modal.configure.sim_groups.title', key: 'sim-groups', message: 'modal.configure.sim_groups.message' }
    ];
    const { isActiveSimCardAlert, isActiveSimPoolAlert, simCardThreshold, simPoolThreshold } = simAlerts;

    // State
    const [simCardsValue, setSimCardsValue] = useState({ value: simCardThreshold, checked: isActiveSimCardAlert });
    const [simGroupsValue, setSimGroupsValue] = useState({ value: simPoolThreshold, checked: isActiveSimPoolAlert });
    const [disabledValidation, setDisabledValidation] = useState(false);

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        storeRefetchSimAlerts(true);
        toast({
            description: <Entity entity='modal.configure.success' namespace={nsOrganization} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.errors.UNKNOWN_ERROR' namespace={nsOrganization} />,
            variant: 'danger'
        });
    };
    const updateSimAlertsSettings = actionsOrganizations.updateSimAlertsSettings(onSuccess, onError);

    // Hooks
    useEffect(() => {
        // Disabling validation button
        // If both alerts are set to false
        if (!simCardsValue.checked && !simGroupsValue.checked) {
            setDisabledValidation(false);
        }
        // Else if one of threshold values is not set
        else if (simCardsValue.value === '' || simGroupsValue.value === '') {
            setDisabledValidation(true);
        }
        else {
            setDisabledValidation(false);
        }
    }, [simCardsValue, simGroupsValue]);

    // Functions
    const getSwitchState = (key) => {
        if (key === 'sim-groups') {
            return simGroupsValue.checked;
        }
        else {
            return simCardsValue.checked;
        }
    };

    const switchAlertCard = (key) => {
        if (key === 'sim-groups') {
            setSimGroupsValue({ ...simGroupsValue, ...{ checked: !simGroupsValue.checked } });
        }
        else {
            setSimCardsValue({ ...simCardsValue, ...{ checked: !simCardsValue.checked } });
        }
    };

    const getSimCardValue = (key) => {
        if (key === 'sim-groups') {
            return simGroupsValue.value;
        }
        else {
            return simCardsValue.value;
        }
    };

    const handleInputChange = (e, key) => {
        const inputValue = e.target.value;
        const regex = /\D$/;

        if (!regex.test(inputValue)) {
            if (key === 'sim-groups') {
                setSimGroupsValue({ ...simGroupsValue, ...{ value: inputValue } });
            }
            else {
                setSimCardsValue({ ...simCardsValue, ...{ value: inputValue } });
            }
        }
    };

    const confirmSimAlertsSettings = () => {
        // This value is only useful to avoid API's breakdowns
        const defaultValueWhenAlertIsNotActive = 0;

        const simCardsThreshold = simCardsValue.value;
        const simGroupsThreshold = simGroupsValue.value;

        const settings = {
            simCardThreshold: simCardsThreshold ? parseInt(simCardsThreshold) : defaultValueWhenAlertIsNotActive,
            isActiveSimCardAlert: simCardsValue.checked,
            simPoolThreshold: simGroupsThreshold ? parseInt(simGroupsThreshold) : defaultValueWhenAlertIsNotActive,
            isActiveSimPoolAlert: simGroupsValue.checked
        };

        updateSimAlertsSettings.mutate({ organizationId: rootOrganization.uuid, settings: settings });
    };

    return <Modal open={isOpen} onOpenChange={setIsOpen}>
        <ModalContent className={cn('max-w-xl')}>
            <HeaderTitle title='modal.configure.title' namespace={nsOrganization} />
            <ModalBody>
                {cardsContent.map((card, index) => (
                    <div key={card.key} className={cn(index !== 0 && 'mt-2')}>
                        <Entity
                            entity={card.title}
                            namespace={nsOrganization}
                            className='text-base text-center font-semibold'
                        />
                        <Card
                            key={card.key}
                            className='card p-4 mt-2 grid gap-2'
                        >
                            <div className='flex gap-[inherit]'>
                                <Switch
                                    name={`configure_${card.key}`}
                                    checked={getSwitchState(card.key)}
                                    onCheckedChange={() => switchAlertCard(card.key)}
                                />
                                <div>
                                    <Entity entity={card.message} namespace={nsOrganization} className='font-light' />
                                </div>
                            </div>
                            <div className='flex gap-[inherit] items-center'>
                                <Entity entity='modal.configure.threshold' namespace={nsOrganization} />
                                <Input
                                    maxLength='3'
                                    className='max-w-14 h-8'
                                    autoFocus={index === 0}
                                    type='tel'
                                    value={getSimCardValue(card.key)}
                                    autoComplete='off'
                                    onChange={(e) => handleInputChange(e, card.key)}
                                />%
                            </div>
                        </Card>
                    </div>
                ))
                }
            </ModalBody>
            <FooterForm
                onSubmit={confirmSimAlertsSettings}
                isSubmitting={updateSimAlertsSettings.isLoading}
                isSubmitDisabled={disabledValidation}
            />
        </ModalContent>
    </Modal>;
}

ModalConfigureAlerts.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired
};

export default ModalConfigureAlerts;